import { addDays, isBefore, isSameDay } from 'date-fns';

/**
 * Canonical accommodation pricing resolver.
 * Priority: special > season > base price.
 */

export interface PriceRule {
  id: string;
  rule_type: 'season' | 'special';
  title: string;
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
  price_ore: number;
  is_active: boolean;
}

export interface NightPrice {
  date: string;        // YYYY-MM-DD
  price_ore: number;
  source: 'special' | 'season' | 'base';
  rule_title?: string;
}

export interface StayPriceSummary {
  nights: NightPrice[];
  total_ore: number;
  has_mixed_pricing: boolean;
  min_price_ore: number;
  max_price_ore: number;
}

/**
 * Get the effective nightly price for a single date.
 */
export function getEffectiveNightPrice(
  dateStr: string,
  basePriceOre: number,
  rules: PriceRule[],
): NightPrice {
  const activeRules = rules.filter(r => r.is_active);

  // Check special rules first (highest priority)
  const special = activeRules.find(
    r => r.rule_type === 'special' && dateStr >= r.start_date && dateStr <= r.end_date,
  );
  if (special) {
    return { date: dateStr, price_ore: special.price_ore, source: 'special', rule_title: special.title };
  }

  // Check season rules
  const season = activeRules.find(
    r => r.rule_type === 'season' && dateStr >= r.start_date && dateStr <= r.end_date,
  );
  if (season) {
    return { date: dateStr, price_ore: season.price_ore, source: 'season', rule_title: season.title };
  }

  return { date: dateStr, price_ore: basePriceOre, source: 'base' };
}

/**
 * Calculate price for a full stay (check-in to check-out).
 * Each night = the check-in date of that night.
 */
export function getStayPrice(
  checkIn: string,
  checkOut: string,
  basePriceOre: number,
  rules: PriceRule[],
): StayPriceSummary {
  const nights: NightPrice[] = [];
  let current = new Date(checkIn + 'T00:00:00');
  const end = new Date(checkOut + 'T00:00:00');

  while (isBefore(current, end) && !isSameDay(current, end)) {
    const dateStr = current.toISOString().slice(0, 10);
    nights.push(getEffectiveNightPrice(dateStr, basePriceOre, rules));
    current = addDays(current, 1);
  }

  const total_ore = nights.reduce((sum, n) => sum + n.price_ore, 0);
  const prices = nights.map(n => n.price_ore);
  const min_price_ore = prices.length > 0 ? Math.min(...prices) : basePriceOre;
  const max_price_ore = prices.length > 0 ? Math.max(...prices) : basePriceOre;
  const has_mixed_pricing = min_price_ore !== max_price_ore;

  return { nights, total_ore, has_mixed_pricing, min_price_ore, max_price_ore };
}

/**
 * Get the display price (minimum applicable) for "Från X kr per natt".
 * Returns öre value.
 */
export function getDisplayPriceOre(basePriceOre: number, rules: PriceRule[]): number {
  const activeRules = rules.filter(r => r.is_active);
  if (activeRules.length === 0) return basePriceOre;

  const allPrices = [basePriceOre, ...activeRules.map(r => r.price_ore)];
  return Math.min(...allPrices);
}

/**
 * Format öre to SEK string.
 */
export function formatOreToKr(ore: number): string {
  const kr = Math.round(ore / 100);
  return kr.toLocaleString('sv-SE');
}

/**
 * Format display price label.
 */
export function formatDisplayPriceLabel(basePriceOre: number | null, rules: PriceRule[]): string | null {
  if (basePriceOre == null || basePriceOre <= 0) return null;
  const displayOre = getDisplayPriceOre(basePriceOre, rules);
  return `Från ${formatOreToKr(displayOre)} kr per natt`;
}

/**
 * Check if rules have seasonal variation.
 */
export function hasSeasonalPricing(rules: PriceRule[]): boolean {
  return rules.some(r => r.is_active);
}

/**
 * Parse price_per_night_text (legacy field) into öre.
 * Returns null if not parseable.
 */
export function parsePriceTextToOre(text: string | null | undefined): number | null {
  if (!text) return null;
  const match = text.trim().match(/(\d+)/);
  return match ? parseInt(match[1], 10) * 100 : null;
}
